import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Spinner, Alert, ProgressBar, Form } from 'react-bootstrap';
import { colors } from '../brand-colors';

// Same slider mapping as backend — kept in sync
const SLIDER_TERMS = {
  density:  [[0,25,'minimal clean'], [26,50,'moderate density'], [51,75,'layered detailed'], [76,100,'dense complex']],
  contrast: [[0,25,'soft muted'], [26,50,'moderate contrast'], [51,75,'bold contrast'], [76,100,'dramatic high-contrast']],
};

function slidersToPrefix(sliders, baseDirection) {
  const terms = [baseDirection];
  for (const [key, ranges] of Object.entries(SLIDER_TERMS)) {
    const val = sliders[key] || 50;
    for (const [low, high, term] of ranges) {
      if (val >= low && val <= high) { terms.push(term); break; }
    }
  }
  return terms.join(', ');
}

const CONTENT_TYPE_MARKERS = [
  { key: 'blog_headers', label: 'Blog Featured Images' },
  { key: 'social_posts', label: 'Social Post Images' },
  { key: 'video_thumbnails', label: 'Video Thumbnails' },
  { key: 'email_headers', label: 'Email Headers' },
];

// Module-level guard — survives component remounts from parent re-renders
let _step1LoadId = 0;

function VisualWorkshop({ onComplete, onSkip, existingResults }) {
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Step 1 state
  const [directions, setDirections] = useState([]);
  const [chosenDirection, setChosenDirection] = useState(null);
  const [sampleTitle, setSampleTitle] = useState('');
  const [sampleSubject, setSampleSubject] = useState('');

  // Step 2 state
  const [contentSamples, setContentSamples] = useState([]);
  const [confirmedConsistent, setConfirmedConsistent] = useState(null);

  // Step 3 state (refinement sliders)
  const [sliders, setSliders] = useState({ density: 50, contrast: 50 });
  const [previewImage, setPreviewImage] = useState(null);

  // Step 4 state (brand marker placement)
  const [brandMarker, setBrandMarker] = useState(null); // null = loading, false = none configured
  const [markerContentTypes, setMarkerContentTypes] = useState({
    blog_headers: true,
    social_posts: false,
    video_thumbnails: true,
    email_headers: true,
  });

  // Load step 1 on mount — use incrementing ID to cancel stale runs
  useEffect(() => {
    if (step === 0) {
      const myId = ++_step1LoadId;
      loadStep1(myId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step1Progress, setStep1Progress] = useState('');

  const loadStep1 = async (loadId) => {
    setGenerating(true);
    setError('');
    setDirections([]);

    // First, get the sample topic (fast Grok call)
    setStep1Progress('Generating sample topic...');
    let subject = null;
    try {
      const topicRes = await fetch('/api/brand-playbook/workshop/step1-topic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (loadId !== _step1LoadId) return; // stale run — abort
      const topicData = await topicRes.json();
      if (topicData.sample_title) setSampleTitle(topicData.sample_title);
      if (topicData.sample_subject) {
        setSampleSubject(topicData.sample_subject);
        subject = topicData.sample_subject;
      }
    } catch (e) {
      console.error('Topic generation failed, using default subject');
    }

    // Then generate each direction image one at a time
    const directionNames = ['Professional Editorial Illustration', 'Photorealistic Editorial', 'Abstract Geometric', 'Mixed-Media Collage'];
    for (let i = 0; i < 4; i++) {
      if (loadId !== _step1LoadId) return; // stale run — abort
      setStep1Progress(`Generating style ${i + 1} of 4: ${directionNames[i]}...`);
      try {
        const res = await fetch('/api/brand-playbook/workshop/step1', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ direction_index: i, sample_subject: subject }),
        });
        if (loadId !== _step1LoadId) return; // stale run — abort
        const data = await res.json();
        if (data.error) { setError(data.error); break; }
        if (data.directions?.length) {
          setDirections(prev => [...prev, ...data.directions]);
        }
      } catch (e) {
        setError(`Failed to generate style direction ${i + 1}`);
        break;
      }
    }
    setStep1Progress('');
    setGenerating(false);
  };

  const [step2Progress, setStep2Progress] = useState('');

  const loadStep2 = async () => {
    if (!chosenDirection) return;
    setGenerating(true);
    setError('');
    setContentSamples([]);

    const contentTypes = ['Blog Header', 'Social Post', 'Video Thumbnail'];
    for (let i = 0; i < contentTypes.length; i++) {
      const ct = contentTypes[i];
      setStep2Progress(`Generating ${ct} (${i + 1} of ${contentTypes.length})...`);
      try {
        const res = await fetch('/api/brand-playbook/workshop/step2', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            direction_description: chosenDirection.description,
            sample_subject: sampleSubject,
            content_type: ct,
          }),
        });
        const data = await res.json();
        if (data.error) { setError(data.error); break; }
        if (data.samples?.length) {
          setContentSamples(prev => [...prev, ...data.samples]);
        }
      } catch (e) {
        setError(`Failed to generate ${ct}`);
        break;
      }
    }
    setStep2Progress('');
    setGenerating(false);
  };

  const loadBrandMarker = async () => {
    try {
      const res = await fetch('/api/brand-playbook/workshop/brand-marker', { credentials: 'include' });
      const data = await res.json();
      if (data.has_marker) {
        setBrandMarker(data);
      } else {
        setBrandMarker(false);
      }
    } catch (e) {
      setBrandMarker(false);
    }
  };

  const loadStep3Preview = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch('/api/brand-playbook/workshop/step4-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sliders,
          direction_description: chosenDirection?.description || 'editorial illustration',
          sample_subject: sampleSubject,
        }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); }
      else { setPreviewImage(data.preview_base64); }
    } catch (e) { setError('Failed to generate preview'); }
    setGenerating(false);
  };

  const handleNext = () => {
    const nextStep = step + 1;
    if (nextStep === 1) { loadStep2(); }
    if (nextStep === 3) { loadBrandMarker(); }
    setStep(nextStep);
  };

  const handleComplete = async () => {
    const baseDirection = chosenDirection?.description || '';
    const computedPrefix = slidersToPrefix(sliders, baseDirection);
    const workshopResults = {
      completed: true,
      completed_at: new Date().toISOString(),
      step1_direction: chosenDirection ? { chosen_index: chosenDirection.index, description: chosenDirection.description } : null,
      step2_content_types: { confirmed_consistent: confirmedConsistent },
      step3_refinement: { ...sliders, style_prefix: computedPrefix },
      brand_marker_usage: {
        has_marker: !!brandMarker,
        content_types: markerContentTypes,
      },
    };

    // Save to backend
    try {
      await fetch('/api/brand-playbook/workshop/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ visual_workshop: workshopResults }),
      });
    } catch (e) {
      console.error('Failed to save workshop:', e);
    }

    onComplete(workshopResults);
  };

  const handleSliderChange = (key, value) => {
    setSliders(prev => ({ ...prev, [key]: parseInt(value) }));
    setPreviewImage(null); // Clear preview when sliders change
  };

  const computedPrefix = slidersToPrefix(sliders, chosenDirection?.description || 'editorial illustration');

  const toggleMarkerContentType = (key) => {
    setMarkerContentTypes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const canProceed = () => {
    if (step === 0) return chosenDirection !== null;
    if (step === 1) return confirmedConsistent !== null;
    if (step === 2) return true; // Sliders always have values
    if (step === 3) return true; // Brand marker step always completable
    return false;
  };

  const stepTitles = ['Style Direction', 'Content Types', 'Refinement', 'Brand Marker'];

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em' }}>
        <strong>Visual Workshop — Step {step + 1} of 4: {stepTitles[step]}</strong>
      </Card.Header>
      <Card.Body className="p-4">
        <ProgressBar
          now={((step + 1) / 4) * 100}
          className="mb-4"
          style={{ height: '6px' }}
        />

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {/* Step 1: Style Direction */}
        {step === 0 && (
          <>
            <h5 className="mb-2">Which visual direction feels right for your brand?</h5>
            <p className="text-muted mb-3">All four images illustrate the same topic in different styles, so you're comparing the visual treatment — not the subject.</p>
            {sampleTitle && (
              <Alert variant="light" style={{ border: `1px solid ${colors.greyLight}`, fontSize: '0.9rem' }}>
                <strong>Sample topic:</strong> <em>{sampleTitle}</em>
              </Alert>
            )}

            {(generating || directions.length > 0) && (
              <>
                <Row>
                  {directions.map(d => (
                    <Col md={6} key={d.index} className="mb-3">
                      <Card
                        onClick={() => setChosenDirection(d)}
                        style={{
                          cursor: 'pointer',
                          border: chosenDirection?.index === d.index ? `3px solid ${colors.goldLight}` : '1px solid #dee2e6',
                          boxShadow: chosenDirection?.index === d.index ? `0 0 12px ${colors.goldLight}40` : 'none',
                          transition: 'all 0.2s',
                        }}
                      >
                        {d.image_base64 ? (
                          <Card.Img
                            variant="top"
                            src={`data:image/jpeg;base64,${d.image_base64}`}
                            style={{ height: '200px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{ height: '200px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span className="text-muted">Image unavailable</span>
                          </div>
                        )}
                        <Card.Body className="p-3">
                          <small className="text-muted">{d.description}</small>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {generating && step1Progress && (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" style={{ color: colors.goldMid }} className="me-2" />
                    <span className="text-muted">{step1Progress}</span>
                  </div>
                )}
              </>
            )}
            {generating && directions.length === 0 && (
              <div className="text-center p-5">
                <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-3" />
                <p className="text-muted">{step1Progress || 'Preparing style directions...'}</p>
              </div>
            )}
          </>
        )}

        {/* Step 2: Content Type Samples */}
        {step === 1 && (
          <>
            <h5 className="mb-2">Your style across content types</h5>
            <p className="text-muted mb-3">
              Your chosen direction — <strong>{chosenDirection?.description}</strong> — applied to three different formats.
              Does the style hold across sizes, or does something feel off?
            </p>
            {sampleTitle && (
              <Alert variant="light" style={{ border: `1px solid ${colors.greyLight}`, fontSize: '0.9rem' }}>
                <strong>Sample topic:</strong> <em>{sampleTitle}</em>
              </Alert>
            )}

            {(generating || contentSamples.length > 0) && (
              <>
                <Row className="align-items-start">
                  {contentSamples.map((s, i) => (
                    <Col md={4} key={i} className="mb-3">
                      <Card>
                        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontSize: '0.85rem', padding: '6px 12px' }}>
                          {s.content_type} ({s.aspect_ratio})
                        </Card.Header>
                        {s.image_base64 ? (
                          <div style={{
                            aspectRatio: s.aspect_ratio === '1:1' ? '1/1' : '16/9',
                            overflow: 'hidden',
                          }}>
                            <Card.Img
                              variant="bottom"
                              src={`data:image/jpeg;base64,${s.image_base64}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                        ) : (
                          <div style={{
                            aspectRatio: s.aspect_ratio === '1:1' ? '1/1' : '16/9',
                            background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <span className="text-muted">Image unavailable</span>
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
                {generating && step2Progress && (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" style={{ color: colors.goldMid }} className="me-2" />
                    <span className="text-muted">{step2Progress}</span>
                  </div>
                )}
                {!generating && contentSamples.length >= 3 && (
                  <div className="d-flex gap-3 align-items-center mt-3">
                    <Button
                      variant="outline-dark"
                      onClick={() => setConfirmedConsistent(true)}
                      style={confirmedConsistent === true ? { backgroundColor: colors.greyDarkest, color: colors.goldLight } : {}}
                    >
                      Looks good, continue →
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => { setStep(0); setConfirmedConsistent(null); setContentSamples([]); }}
                    >
                      ← Pick a different style
                    </Button>
                  </div>
                )}
              </>
            )}
            {generating && contentSamples.length === 0 && (
              <div className="text-center p-5">
                <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-3" />
                <p className="text-muted">{step2Progress || 'Generating content type samples...'}</p>
              </div>
            )}
          </>
        )}

        {/* Step 3: Refinement Sliders */}
        {step === 2 && (
          <>
            <h5 className="mb-2">Fine-tune your visual style</h5>
            <p className="text-muted mb-3">
              Refining your <strong>{chosenDirection?.description}</strong> style. Adjust sliders, then hit Preview to see the effect.
            </p>
            {sampleTitle && (
              <Alert variant="light" style={{ border: `1px solid ${colors.greyLight}`, fontSize: '0.9rem' }} className="mb-4">
                <strong>Preview topic:</strong> <em>{sampleTitle}</em>
              </Alert>
            )}

            <Row>
              <Col md={8}>
                {[
                  { key: 'density', left: 'Cleaner', right: 'Busier' },
                  { key: 'contrast', left: 'Softer', right: 'Bolder' },
                ].map(({ key, left, right }) => (
                  <div key={key} className="mb-4">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">{left}</small>
                      <strong style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>{key}</strong>
                      <small className="text-muted">{right}</small>
                    </div>
                    <Form.Range
                      min={0}
                      max={100}
                      value={sliders[key]}
                      onChange={(e) => handleSliderChange(key, e.target.value)}
                    />
                  </div>
                ))}

                <Card className="mt-3" style={{ border: `1px solid ${colors.goldDark}` }}>
                  <Card.Body className="p-3">
                    <small className="text-muted d-block mb-1">Computed Style Prefix:</small>
                    <code style={{ fontSize: '0.85rem', color: colors.greyDarkest }}>{computedPrefix}</code>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Button
                  variant="outline-dark"
                  onClick={loadStep3Preview}
                  disabled={generating}
                  className="w-100 mb-3"
                >
                  {generating ? <><Spinner size="sm" className="me-2" />Generating...</> : 'Preview Image'}
                </Button>
                {previewImage && (
                  <Card>
                    <Card.Img
                      src={`data:image/jpeg;base64,${previewImage}`}
                      style={{ objectFit: 'cover' }}
                    />
                  </Card>
                )}
              </Col>
            </Row>
          </>
        )}

        {/* Step 4: Brand Marker Placement */}
        {step === 3 && (
          <>
            <h5 className="mb-2">Brand Marker Placement</h5>
            <p className="text-muted mb-4">Your brand marker is the visual signature that makes your content instantly recognizable. Choose which content types get the marker overlay.</p>

            {brandMarker === null ? (
              <div className="text-center p-4">
                <Spinner animation="border" size="sm" style={{ color: colors.goldMid }} className="me-2" />
                <span className="text-muted">Loading brand marker...</span>
              </div>
            ) : brandMarker === false ? (
              <Alert variant="warning">
                <strong>No Brand Marker configured yet.</strong> You can set one up at any time — it won't block playbook generation.
                <div className="mt-2">
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => window.open('/dashboard/iconography', '_blank')}
                  >
                    Set Up Brand Marker →
                  </Button>
                </div>
              </Alert>
            ) : (
              <>
                <Row className="mb-4">
                  <Col md={3}>
                    <Card>
                      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontSize: '0.8rem', padding: '6px 12px' }}>
                        Your Brand Marker
                      </Card.Header>
                      <Card.Body className="text-center p-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <img
                          src={brandMarker.primary_image}
                          alt="Brand Marker"
                          style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }}
                        />
                        <div className="mt-2">
                          <small className="text-muted">
                            {brandMarker.position} &middot; {brandMarker.size} &middot; {brandMarker.opacity}% opacity
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-1 text-muted p-0"
                      onClick={() => window.open('/dashboard/iconography', '_blank')}
                      style={{ fontSize: '0.8rem' }}
                    >
                      Change marker settings →
                    </Button>
                  </Col>
                  <Col md={9}>
                    <p className="mb-3"><strong>Apply brand marker to:</strong></p>
                    {CONTENT_TYPE_MARKERS.map(({ key, label }) => (
                      <div key={key} className="mb-2">
                        <Form.Check
                          type="switch"
                          id={`marker-${key}`}
                          label={label}
                          checked={markerContentTypes[key]}
                          onChange={() => toggleMarkerContentType(key)}
                          style={{ fontSize: '1rem' }}
                        />
                      </div>
                    ))}
                    <small className="text-muted d-block mt-3">
                      The marker position, size, and opacity are configured in your Brand Marker settings.
                      This controls which content types receive the overlay during image generation.
                    </small>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}

        {/* Navigation */}
        <div className="d-flex justify-content-between mt-4 pt-3" style={{ borderTop: `1px solid ${colors.greyLight}` }}>
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => step > 0 ? setStep(step - 1) : onSkip()}
              disabled={generating}
            >
              {step > 0 ? '← Back' : 'Cancel'}
            </Button>
            {existingResults && (
              <Button
                variant="link"
                className="ms-2 text-muted"
                onClick={onSkip}
                style={{ fontSize: '0.85rem' }}
              >
                Skip Workshop
              </Button>
            )}
          </div>
          <Button
            variant="outline-dark"
            onClick={step === 3 ? handleComplete : handleNext}
            disabled={!canProceed() || generating}
          >
            {step === 3 ? 'Complete Workshop' : 'Next →'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

export default VisualWorkshop;
